// Domain 02 network builders and diagram layouts.
import { pc, server, sw, router, link, network, setIf } from '../../engine/net/model.js';
import { createInterface } from '../../engine/ios/switching.js';

export const VLANS = {
  10: { name: 'SALES', net: '10.10.10', color: 'vlan10' },
  20: { name: 'SUPPORT', net: '10.10.20', color: 'vlan20' },
  30: { name: 'SERVERS', net: '10.10.30' },
  99: { name: 'MGMT', net: '10.10.99' },
};
const vl = (...ids) => Object.fromEntries(ids.map((v) => [v, { name: VLANS[v].name }]));

export function mkSwitch(id, { seq, l3 = false, vlans = [], gigPorts = 2 } = {}) {
  return sw(id, { seq, l3, gigPorts, vlans: vl(...vlans) });
}
export function access(s, port, vlan, description = '') {
  setIf(s, port, { mode: 'access', accessVlan: vlan, explicitAccess: true, description });
}
export function trunk(s, port, { native, allowed, description = '' } = {}) {
  setIf(s, port, { mode: 'trunk', description, ...(native ? { nativeVlan: native } : {}), ...(allowed ? { allowed: [...allowed] } : {}), ...(s.l3 ? { trunkEncap: 'dot1q' } : {}) });
}
export function svi(s, v, ip, { up = true } = {}) {
  const i = createInterface(s, `Vlan${v}`);
  Object.assign(i, { ip: ip ? { ip, mask: '255.255.255.0' } : null, shutdown: !up });
  return i;
}
/** Bundle ports into Port-channel <id>, copying the member config to the Po like IOS does. */
export function bundle(s, id, ports, mode = 'active') {
  const po = createInterface(s, `Port-channel${id}`);
  const m0 = s.interfaces[ports[0]];
  Object.assign(po, { mode: m0.mode, accessVlan: m0.accessVlan, nativeVlan: m0.nativeVlan, allowed: m0.allowed ? [...m0.allowed] : undefined, trunkEncap: m0.trunkEncap, description: m0.description });
  for (const p of ports) s.interfaces[p].channelGroup = { id, mode };
  return po;
}
export function host(id, vlan, n, { mac, gw = true } = {}) {
  const net = VLANS[vlan].net;
  return pc(id, { mac, ip: `${net}.${n}`, mask: '255.255.255.0', gw: gw ? `${net}.1` : null, dns: [] });
}
const macs = { PC1: '0050796a1001', PC2: '0050796a2002', PC3: '0050796a1003', PC4: '0050796a2004', PC5: '0050796a1005', PC6: '0050796a2006' };

// ------------------------------------------------------------ single switch, two VLANs
export function oneSwitch({ configured = true } = {}) {
  const SW1 = mkSwitch('SW1', { seq: 21, vlans: configured ? [10, 20] : [] });
  const PC1 = host('PC1', 10, 11, { mac: macs.PC1 });
  const PC2 = host('PC2', 10, 12, { mac: '0050796a1002' });
  const PC3 = host('PC3', 20, 11, { mac: '0050796a2003' });
  const PC4 = host('PC4', 20, 12, { mac: macs.PC4 });
  // Deliberately the SAME subnet for both departments before VLANs exist, so the
  // broadcast-domain lesson is visible: 10.10.10.0/24 and 10.10.20.0/24 are distinct,
  // but without VLANs ARP from PC1 still floods to PC3 and PC4.
  for (const [p, port] of [['PC1', 1], ['PC2', 2], ['PC3', 11], ['PC4', 12]]) setIf(SW1, `FastEthernet0/${port}`, { description: `${p}` });
  if (configured) {
    access(SW1, 'FastEthernet0/1', 10, 'PC1-SALES'); access(SW1, 'FastEthernet0/2', 10, 'PC2-SALES');
    access(SW1, 'FastEthernet0/11', 20, 'PC3-SUPPORT'); access(SW1, 'FastEthernet0/12', 20, 'PC4-SUPPORT');
  }
  return network([SW1, PC1, PC2, PC3, PC4], [
    link('PC1', 'FastEthernet0', 'SW1', 'FastEthernet0/1'), link('PC2', 'FastEthernet0', 'SW1', 'FastEthernet0/2'),
    link('PC3', 'FastEthernet0', 'SW1', 'FastEthernet0/11'), link('PC4', 'FastEthernet0', 'SW1', 'FastEthernet0/12'),
  ]);
}
export const oneSwitchLayout = {
  width: 900, height: 330,
  nodes: {
    SW1: { x: 450, y: 90, type: 'switch', sub: 'Catalyst 2960 · floor 2' },
    PC1: { x: 110, y: 260, type: 'pc', sub: '10.10.10.11 · Sales' }, PC2: { x: 300, y: 260, type: 'pc', sub: '10.10.10.12 · Sales' },
    PC3: { x: 600, y: 260, type: 'pc', sub: '10.10.20.11 · Support' }, PC4: { x: 790, y: 260, type: 'pc', sub: '10.10.20.12 · Support' },
  },
  zones: [{ x: 30, y: 200, w: 380, h: 115, label: 'VLAN 10 · SALES · 10.10.10.0/24', cls: 'vlan10' }, { x: 490, y: 200, w: 380, h: 115, label: 'VLAN 20 · SUPPORT · 10.10.20.0/24', cls: 'vlan20' }],
};

// ------------------------------------------------------------ two switches over a trunk
export function twoSwitch({ trunkUp = true, native = 99, allowed = [10, 20, 99] } = {}) {
  const SW1 = mkSwitch('SW1', { seq: 21, vlans: [10, 20, 99] });
  const SW2 = mkSwitch('SW2', { seq: 22, vlans: [10, 20, 99] });
  access(SW1, 'FastEthernet0/1', 10, 'PC1-SALES'); access(SW1, 'FastEthernet0/11', 20, 'PC2-SUPPORT');
  access(SW2, 'FastEthernet0/1', 10, 'PC3-SALES'); access(SW2, 'FastEthernet0/11', 20, 'PC4-SUPPORT');
  setIf(SW1, 'GigabitEthernet0/1', { description: 'TO-SW2-Gi0/1' });
  setIf(SW2, 'GigabitEthernet0/1', { description: 'TO-SW1-Gi0/1' });
  if (trunkUp) { trunk(SW1, 'GigabitEthernet0/1', { native, allowed, description: 'TRUNK-SW2' }); trunk(SW2, 'GigabitEthernet0/1', { native, allowed, description: 'TRUNK-SW1' }); }
  svi(SW1, 99, '10.10.99.11'); svi(SW2, 99, '10.10.99.12');
  const PC1 = host('PC1', 10, 11, { mac: macs.PC1 }), PC2 = host('PC2', 20, 11, { mac: '0050796a2011' });
  const PC3 = host('PC3', 10, 21, { mac: '0050796a1021' }), PC4 = host('PC4', 20, 21, { mac: macs.PC4 });
  return network([SW1, SW2, PC1, PC2, PC3, PC4], [
    link('PC1', 'FastEthernet0', 'SW1', 'FastEthernet0/1'), link('PC2', 'FastEthernet0', 'SW1', 'FastEthernet0/11'),
    link('PC3', 'FastEthernet0', 'SW2', 'FastEthernet0/1'), link('PC4', 'FastEthernet0', 'SW2', 'FastEthernet0/11'),
    link('SW1', 'GigabitEthernet0/1', 'SW2', 'GigabitEthernet0/1'),
  ]);
}
export const twoSwitchLayout = {
  width: 940, height: 350,
  nodes: {
    SW1: { x: 260, y: 110, type: 'switch', sub: 'Floor 2 · Mgmt 10.10.99.11' },
    SW2: { x: 680, y: 110, type: 'switch', sub: 'Floor 3 · Mgmt 10.10.99.12' },
    PC1: { x: 120, y: 280, type: 'pc', sub: '10.10.10.11 · VLAN 10' }, PC2: { x: 330, y: 280, type: 'pc', sub: '10.10.20.11 · VLAN 20' },
    PC3: { x: 610, y: 280, type: 'pc', sub: '10.10.10.21 · VLAN 10' }, PC4: { x: 820, y: 280, type: 'pc', sub: '10.10.20.21 · VLAN 20' },
  },
  zones: [{ x: 30, y: 30, w: 420, h: 305, label: 'FLOOR 2' }, { x: 490, y: 30, w: 420, h: 305, label: 'FLOOR 3' }],
  linkNotes: { 'SW1:GigabitEthernet0/1--SW2:GigabitEthernet0/1': '802.1Q · native 99 · allowed 10,20,99' },
};

// ------------------------------------------------------------ router-on-a-stick
export function roas({ routerDone = false } = {}) {
  const n = twoSwitch();
  const SW1 = n.devices.SW1;
  const R1 = router('R1', { seq: 7 });
  trunk(SW1, 'GigabitEthernet0/2', { native: 99, allowed: [10, 20, 99], description: 'TRUNK-R1-G0/0' });
  if (routerDone) {
    setIf(R1, 'GigabitEthernet0/0', { shutdown: false, description: 'TRUNK-SW1' });
    for (const [v, nat] of [[10, false], [20, false], [99, true]]) {
      const s = createInterface(R1, `GigabitEthernet0/0.${v}`);
      Object.assign(s, { encap: { vlan: v, native: nat }, ip: { ip: `${VLANS[v].net}.1`, mask: '255.255.255.0' }, description: VLANS[v].name });
    }
  }
  n.devices.R1 = R1;
  n.links.push(link('SW1', 'GigabitEthernet0/2', 'R1', 'GigabitEthernet0/0'));
  for (const d of [SW1, n.devices.SW2]) d.defaultGateway = '10.10.99.1';
  return n;
}
export const roasLayout = {
  ...twoSwitchLayout, height: 390,
  nodes: {
    ...Object.fromEntries(Object.entries(twoSwitchLayout.nodes).map(([k, v]) => [k, { ...v, y: v.y + 60 }])),
    R1: { x: 260, y: 55, type: 'router', sub: 'G0/0.10 · G0/0.20 · G0/0.99' },
  },
  zones: [{ x: 30, y: 100, w: 420, h: 280, label: 'FLOOR 2' }, { x: 490, y: 100, w: 420, h: 280, label: 'FLOOR 3' }],
  linkLabels: { 'R1:GigabitEthernet0/0': '.10 .20 .99 → .1' },
};

// ------------------------------------------------------------ STP triangle
export function triangle({ vlans = [10, 99] } = {}) {
  const SW1 = mkSwitch('SW1', { seq: 0x31, vlans });
  const SW2 = mkSwitch('SW2', { seq: 0x12, vlans });
  const SW3 = mkSwitch('SW3', { seq: 0x23, vlans });
  const T = (s, p, d) => trunk(s, p, { native: 99, allowed: vlans, description: d });
  T(SW1, 'GigabitEthernet0/1', 'TO-SW2'); T(SW1, 'GigabitEthernet0/2', 'TO-SW3');
  T(SW2, 'GigabitEthernet0/1', 'TO-SW1'); T(SW2, 'GigabitEthernet0/2', 'TO-SW3');
  T(SW3, 'GigabitEthernet0/1', 'TO-SW1'); T(SW3, 'GigabitEthernet0/2', 'TO-SW2');
  svi(SW1, 99, '10.10.99.11'); svi(SW2, 99, '10.10.99.12'); svi(SW3, 99, '10.10.99.13');
  access(SW2, 'FastEthernet0/1', 10, 'PC1-SALES'); access(SW3, 'FastEthernet0/1', 10, 'PC2-SALES');
  const PC1 = host('PC1', 10, 21, { mac: macs.PC1 }), PC2 = host('PC2', 10, 31, { mac: '0050796a1031' });
  return network([SW1, SW2, SW3, PC1, PC2], [
    link('SW1', 'GigabitEthernet0/1', 'SW2', 'GigabitEthernet0/1'),
    link('SW1', 'GigabitEthernet0/2', 'SW3', 'GigabitEthernet0/1'),
    link('SW2', 'GigabitEthernet0/2', 'SW3', 'GigabitEthernet0/2'),
    link('PC1', 'FastEthernet0', 'SW2', 'FastEthernet0/1'),
    link('PC2', 'FastEthernet0', 'SW3', 'FastEthernet0/1'),
  ]);
}
export const triangleLayout = (sub = {}) => ({
  width: 900, height: 420,
  nodes: {
    SW1: { x: 450, y: 70, type: 'switch', sub: sub.SW1 || 'Distribution' },
    SW2: { x: 220, y: 260, type: 'switch', sub: sub.SW2 || 'Access A' },
    SW3: { x: 680, y: 260, type: 'switch', sub: sub.SW3 || 'Access B' },
    PC1: { x: 90, y: 370, type: 'pc', sub: '10.10.10.21 · VLAN 10' },
    PC2: { x: 810, y: 370, type: 'pc', sub: '10.10.10.31 · VLAN 10' },
  },
  zones: [{ x: 20, y: 20, w: 860, h: 390, label: 'CAMPUS BLOCK · trunks: native 99, allowed 10,99' }],
});

// ------------------------------------------------------------ EtherChannel pair
export function ecPair({ bundled = false } = {}) {
  const n = twoSwitch();
  const [SW1, SW2] = [n.devices.SW1, n.devices.SW2];
  // Replace the single Gi0/1 trunk with a two-link bundle on Gi0/1–Gi0/2.
  for (const s of [SW1, SW2]) {
    trunk(s, 'GigabitEthernet0/1', { native: 99, allowed: [10, 20, 99], description: 'UPLINK-A' });
    trunk(s, 'GigabitEthernet0/2', { native: 99, allowed: [10, 20, 99], description: 'UPLINK-B' });
    if (bundled) bundle(s, 1, ['GigabitEthernet0/1', 'GigabitEthernet0/2'], s.id === 'SW1' ? 'active' : 'passive');
  }
  n.links.push(link('SW1', 'GigabitEthernet0/2', 'SW2', 'GigabitEthernet0/2'));
  return n;
}
export const ecLayout = {
  ...twoSwitchLayout,
  linkNotes: {},
  notes: [{ x: 470, y: 78, text: 'Po1 · LACP · 2 × Gi', anchor: 'middle', cls: 'good' }],
};

// ------------------------------------------------------------ multilayer switch
export function mls({ routed = false } = {}) {
  const MLS1 = mkSwitch('MLS1', { seq: 0x01, l3: true, vlans: [10, 20, 99] });
  const SW2 = mkSwitch('SW2', { seq: 0x42, vlans: [10, 20, 99] });
  trunk(MLS1, 'GigabitEthernet0/1', { native: 99, allowed: [10, 20, 99], description: 'TO-SW2' });
  trunk(SW2, 'GigabitEthernet0/1', { native: 99, allowed: [10, 20, 99], description: 'TO-MLS1' });
  access(SW2, 'FastEthernet0/1', 10, 'PC1-SALES'); access(SW2, 'FastEthernet0/11', 20, 'PC2-SUPPORT');
  svi(SW2, 99, '10.10.99.12'); SW2.defaultGateway = '10.10.99.1';
  if (routed) { MLS1.ipRouting = true; svi(MLS1, 10, '10.10.10.1'); svi(MLS1, 20, '10.10.20.1'); svi(MLS1, 99, '10.10.99.1'); }
  const PC1 = host('PC1', 10, 11, { mac: macs.PC1 }), PC2 = host('PC2', 20, 11, { mac: '0050796a2011' });
  return network([MLS1, SW2, PC1, PC2], [
    link('MLS1', 'GigabitEthernet0/1', 'SW2', 'GigabitEthernet0/1'),
    link('PC1', 'FastEthernet0', 'SW2', 'FastEthernet0/1'), link('PC2', 'FastEthernet0', 'SW2', 'FastEthernet0/11'),
  ]);
}
export const mlsLayout = {
  width: 900, height: 380,
  nodes: {
    MLS1: { x: 450, y: 70, type: 'switch', sub: 'Catalyst 3560 · multilayer' },
    SW2: { x: 450, y: 200, type: 'switch', sub: 'Access · Mgmt 10.10.99.12' },
    PC1: { x: 250, y: 320, type: 'pc', sub: '10.10.10.11 · VLAN 10 · gw .1' },
    PC2: { x: 650, y: 320, type: 'pc', sub: '10.10.20.11 · VLAN 20 · gw .1' },
  },
  zones: [{ x: 60, y: 260, w: 380, h: 110, label: 'VLAN 10 · SALES', cls: 'vlan10' }, { x: 460, y: 260, w: 380, h: 110, label: 'VLAN 20 · SUPPORT', cls: 'vlan20' }],
  notes: [{ x: 560, y: 60, text: 'SVIs: Vlan10 .1 · Vlan20 .1 · Vlan99 .1', cls: 'good' }],
};
export { server, setIf, link, network, pc };
